import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@store/hooks';
import { logout } from '@store/slices/authSlice';
import { authService } from '@/services/authService';
import { APP_ROUTES } from '@/config/routes';

interface UseInactivityLogoutOptions {
  /** Time in milliseconds before showing warning. Default: 25 minutes (1500000ms) */
  warningTimeout?: number;
  /** Time in milliseconds after warning before auto-logout. Default: 5 minutes (300000ms) */
  logoutTimeout?: number;
  /** Enable/disable the inactivity logout feature. Default: true */
  enabled?: boolean;
}

interface InactivityState {
  showWarning: boolean;
  timeRemaining: number;
}

const DEFAULT_WARNING_TIMEOUT = 25 * 60 * 1000; // 25 minutes
const DEFAULT_LOGOUT_TIMEOUT = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to handle user inactivity logout with polite warning
 * Tracks mouse movement, keyboard input, clicks, and touch events
 */
export const useInactivityLogout = (options: UseInactivityLogoutOptions = {}) => {
  const {
    warningTimeout = DEFAULT_WARNING_TIMEOUT,
    logoutTimeout = DEFAULT_LOGOUT_TIMEOUT,
    enabled = true,
  } = options;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [state, setState] = useState<InactivityState>({
    showWarning: false,
    timeRemaining: 0,
  });

  const warningTimerRef = useRef<number | null>(null);
  const logoutTimerRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const warningTimeRef = useRef<number>(0);
  const showWarningRef = useRef<boolean>(false);

  useEffect(() => {
    showWarningRef.current = state.showWarning;
  }, [state.showWarning]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(async () => {
    clearAllTimers();
    setState({ showWarning: false, timeRemaining: 0 });

    try {
      await authService.logout();
      dispatch(logout());
      navigate(APP_ROUTES.LOGIN, {
        state: { message: 'You have been logged out due to inactivity.' },
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Error during inactivity logout:', msg);
      // Force navigation even if logout fails
      dispatch(logout());
      navigate(APP_ROUTES.LOGIN);
    }
  }, [clearAllTimers, dispatch, navigate]);

  // Start countdown when warning is shown
  const startCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    warningTimeRef.current = Date.now();

    // Update countdown every second
    countdownIntervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - warningTimeRef.current;
      const remaining = Math.max(0, Math.floor((logoutTimeout - elapsed) / 1000));

      setState(prev => ({ ...prev, timeRemaining: remaining }));

      if (remaining <= 0) {
        handleLogout();
      }
    }, 1000);
  }, [logoutTimeout, handleLogout]);

  // Show warning and start logout timer
  const showWarningModal = useCallback(() => {
    setState({
      showWarning: true,
      timeRemaining: Math.floor(logoutTimeout / 1000),
    });

    startCountdown();

    // Set logout timer
    logoutTimerRef.current = window.setTimeout(() => {
      handleLogout();
    }, logoutTimeout);
  }, [logoutTimeout, startCountdown, handleLogout]);

  // Start inactivity timer
  const startInactivityTimer = useCallback(() => {
    clearAllTimers();

    warningTimerRef.current = window.setTimeout(() => {
      showWarningModal();
    }, warningTimeout);
  }, [warningTimeout, showWarningModal, clearAllTimers]);

  // User chose to stay active
  const handleStayActive = useCallback(() => {
    clearAllTimers();
    setState({ showWarning: false, timeRemaining: 0 });
    // Restart the inactivity timer
    startInactivityTimer();
  }, [clearAllTimers, startInactivityTimer]);

  // Reset timer on user activity
  const resetTimer = useCallback(() => {
    // Only reset if warning is not shown
    if (!showWarningRef.current) {
      startInactivityTimer();
    }
  }, [startInactivityTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      // Ensure effect returns a cleanup function in the disabled branch
      return () => {};
    }

    // Events that indicate user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle reset timer to avoid excessive calls
    let throttleTimeout: number | null = null;
    const throttledResetTimer = () => {
      if (!throttleTimeout) {
        throttleTimeout = window.setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledResetTimer, { passive: true });
    });

    // Start initial timer
    startInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledResetTimer);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      clearAllTimers();
    };
  }, [enabled, resetTimer, startInactivityTimer, clearAllTimers]);

  return {
    showWarning: state.showWarning,
    timeRemaining: state.timeRemaining,
    handleStayActive,
    handleLogout,
  };
};
